"use client";

import React from "react";
import { Mail, MapPin, Clock, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inputClasses = "flex h-10 w-full border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
const textareaClasses = "flex min-h-[120px] w-full border-b border-input bg-transparent px-0 py-2 text-sm ring-offset-background placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-300";
export default function InquireCollaborate() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="11398" id="start-your-strategy" ref={ref} className={`relative w-full dark bg-background py-24 transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-24">
          {}
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-4">
              <h2 className="font-serif text-4xl font-bold tracking-tighter text-foreground sm:text-6xl">
                Let’s Craft Your Future
              </h2>
              <p className="max-w-md text-xl leading-relaxed text-muted-foreground">
                Your path to non-traditional wealth begins with a conversation. 
                Connect with our advisors to architect your bespoke financial strategy.
              </p>
            </div>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary shadow-[0_0_15px_rgba(255,191,0,0.2)]">
                  <MapPin className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-primary">
                    Strategic Hub
                  </h3>
                  <p className="mt-1 font-serif text-lg text-foreground">3333 Michelson Dr. Suite 300<br />Irvine, CA 92612</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary shadow-[0_0_15px_rgba(255,191,0,0.2)]">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-primary">
                    Secure Channel
                  </h3>
                  <p className="mt-1 font-serif text-lg text-foreground">admin@ftgfin.com</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary shadow-[0_0_15px_rgba(255,191,0,0.2)]">
                  <Clock className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-mono text-xs font-bold uppercase tracking-widest text-primary">
                    Advisory Hours
                  </h3>
                  <p className="mt-1 font-serif text-lg text-foreground">Monday — Friday, 9am - 6pm PST<br /><span className="text-sm italic text-accent-foreground opacity-90" data-ast-id="f:/src/pages/Contact/sections/InquireCollaborate.tsx|sig:7xkbfu" data-ast-start="4184" data-ast-end="4345">Strategy sessions available by appointment.</span></p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl lg:p-10">
            <div className="mb-8">
              <h3 className="font-serif text-3xl font-medium text-foreground">
                Strategic Inquiry
              </h3>
              <p className="mt-2 font-sans text-sm text-muted-foreground">
                Please provide details about your financial goals or specific interests.
              </p>
            </div>

            <FormProvider formKey="start_strategy_form_contact" formId="start_strategy_form_contact" sectionName="inquire-collaborate" defaultValues={{
            name: "",
            email: "",
            affiliation: "",
            interest: "",
            message: ""
          }} validationRules={{
            name: {
              required: "Full name is required"
            },
            email: {
              required: "Email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address"
              }
            },
            message: {
              required: "Please share your vision or questions"
            }
          }} submitText="Begin Your Strategy" successMessage="Strategy Request Received. We will reach out to craft your plan within 48 hours.">
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-6 rounded-xl bg-accent/20 p-8 text-center border border-accent/30">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-[0_0_20px_rgba(0,255,160,0.3)]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-8 w-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h4 className="font-serif text-2xl font-medium text-foreground">
          Strategy Initiated
        </h4>
        <p className="text-muted-foreground max-w-xs mx-auto">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/40 text-primary hover:bg-primary/10 hover:border-primary transition-all duration-300">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-primary">Full Name</FormLabel>
            <FormControl>
              <input {...field} type="text" placeholder="John Doe" className={inputClasses} />
            </FormControl>
            <p className="text-[0.7rem] text-muted-foreground/60 italic">
              How should we address you?
            </p>
            <FormMessage className="text-destructive text-[0.7rem]" />
          </FormItem>} />

      <FormField control={control} name="email" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-primary">Email Address</FormLabel>
            <FormControl>
              <input {...field} type="email" placeholder="john@wealthcraft.com" className={inputClasses} />
            </FormControl>
            <p className="text-[0.7rem] text-muted-foreground/60 italic">
              Your secure communication line.
            </p>
            <FormMessage className="text-destructive text-[0.7rem]" />
          </FormItem>} />

      <FormField control={control} name="affiliation" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-primary">Current Firm (Optional)</FormLabel>
            <FormControl>
              <input {...field} type="text" placeholder="e.g., Fidelity, Vanguard" className={inputClasses} />
            </FormControl>
            <p className="text-[0.7rem] text-muted-foreground/60 italic">
              Where your assets are currently managed.
            </p>
            <FormMessage className="text-destructive text-[0.7rem]" />
          </FormItem>} />

      <FormField control={control} name="interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-primary">Strategy Interest</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="border-b border-t-0 border-l-0 border-r-0 rounded-none bg-transparent px-0 focus:ring-0 focus:border-primary transition-all duration-300">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                {["Creative Finance", "Life Insurance with Living Benefits", "Hybrid Annuities", "Wealth Strategy Audit", "Other"].map((option, i) => <SelectItem key={i} value={option} data-index={i} className="focus:bg-primary/10 focus:text-primary">
                    {option}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[0.7rem] text-muted-foreground/60 italic">
              Select the primary area you wish to explore.
            </p>
            <FormMessage className="text-destructive text-[0.7rem]" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[10px] uppercase tracking-widest text-primary">Your Financial Vision</FormLabel>
            <FormControl>
              <textarea {...field} placeholder="Share your goals, questions, or specific needs..." className={textareaClasses} />
            </FormControl>
            <p className="text-[0.7rem] text-muted-foreground/60 italic">
              Provide context for our initial consultation.
            </p>
            <FormMessage className="text-destructive text-[0.7rem]" />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive border border-destructive/20">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-14 w-full bg-gradient-to-r from-primary via-[#FFD700] to-primary bg-[length:200%_auto] hover:bg-[right_center] text-base font-bold text-primary-foreground transition-all duration-500 shadow-xl shadow-primary/20 rounded-lg">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </> : "Begin Your Strategy"}
      </Button>
    </Form>;
}